import { axiosInstance } from "../services/apiService";
import type { AxiosRequestConfig } from "axios";
import type { StepUpHandling } from "../services/stepUp";

/**
 * Per-request handling the caller asks for beyond URL, method, body and headers.
 * `stepUp: "host"` hands a step-up refusal to the operator's one prompt instead
 * of rejecting — only for a caller with no code field of its own (see
 * services/stepUp.ts, StepUpHandling). Carried on the axios config, never a header.
 */
export interface FetcherOptions {
  stepUp?: StepUpHandling;
  stepUpAction?: string;
}

interface V2SuccessBody<TData = unknown> {
  success: true;
  data: TData;
}

interface LegacyBody<TData = unknown> {
  status?: boolean;
  data?: TData;
}

type ResponseBody<TData = unknown> = V2SuccessBody<TData> | LegacyBody<TData>;

function isV2Success<TData>(body: unknown): body is V2SuccessBody<TData> {
  return (
    typeof body === "object" &&
    body !== null &&
    "success" in body &&
    (body as V2SuccessBody<TData>).success === true
  );
}

function isLegacyWrapped<TData>(body: unknown): body is LegacyBody<TData> {
  return (
    typeof body === "object" &&
    body !== null &&
    ("status" in body || "data" in body) &&
    !("success" in body)
  );
}

/**
 * One request, one place for error normalisation. Both `fetcher` and
 * `fetchEnvelope` go through this so they cannot drift.
 */
const request = async <TRequest>(
  url: string,
  method: string,
  requestData?: TRequest,
  headers?: Record<string, string>,
  options?: FetcherOptions
): Promise<unknown> => {
  try {
    const config: AxiosRequestConfig = {
      method,
      url,
      ...(requestData && { data: requestData }),
      ...(headers && { headers }),
      ...(options?.stepUp && { stepUp: options.stepUp }),
      ...(options?.stepUpAction && { stepUpAction: options.stepUpAction }),
    };

    const response = await axiosInstance(config);
    return response.data;
  } catch (error) {
    // The axios interceptor already converts errors to Error objects,
    // but handle edge cases for safety
    if (error instanceof Error) {
      throw error;
    }

    if (typeof error === "string") {
      throw new Error(error);
    }

    throw new Error("An unknown error occurred");
  }
};

/**
 * 🔴 THIS DISCARDS EVERYTHING BESIDE `data`, AND THAT IS THE POINT — but it is
 * also a trap worth knowing about.
 *
 * A v2 response is `{ success, data, ...anything else }`. Unwrapping to `data`
 * is what almost every caller wants, and it is why the console reads so
 * cleanly. What it also means is that any SIBLING of `data` — pagination
 * metadata being the one that keeps coming up — never reaches the caller. The
 * server can send `pagination.total` and `pagination.nextCursor`, and through
 * this function they simply do not exist.
 *
 * That has now cost two features. `dposAdminService` and the press list both
 * needed the page metadata and both worked around it with a bare `axios` call,
 * which is worse than it looks: bare axios uses `axios.defaults` rather than
 * `axiosInstance`, so it also skips the interceptor this file's own catch
 * block relies on for error normalisation.
 *
 * ⇒ If you need the envelope, use `fetchEnvelope` below. Do not hand-roll a
 * request to get at it.
 */
export const fetcher = async <TResponse, TRequest = undefined>(
  url: string,
  method = "GET",
  requestData?: TRequest,
  headers?: Record<string, string>,
  options?: FetcherOptions
): Promise<TResponse> => {
  const body = (await request(url, method, requestData, headers, options)) as
    | ResponseBody<TResponse>
    | TResponse;

  // v2 format: { success: true, data: ... } - unwrap the data
  if (isV2Success<TResponse>(body)) {
    return body.data;
  }

  // Legacy format with status/data wrapper - unwrap the data
  if (isLegacyWrapped<TResponse>(body) && body.data !== undefined) {
    return body.data as TResponse;
  }

  // Raw data (no wrapper) - return as-is
  return body as TResponse;
};

/**
 * The whole response body, unwrapped by nobody.
 *
 * For the handful of endpoints whose answer is not only `data` — a paged list
 * that also reports `pagination.total`, `hasMore` and `nextCursor`. Reach for
 * `fetcher` everywhere else; a caller that takes the envelope and then only
 * reads `.data` has just written `fetcher` with extra steps.
 *
 * It goes through `axiosInstance` and the same error normalisation as
 * `fetcher`, which is the reason to use it rather than a bare `axios.get`:
 * a bare call leans on `axios.defaults` for the base URL and auth header and
 * SKIPS the interceptor, so its failures arrive in a different shape from
 * every other request in the console.
 *
 * @example
 * const body = await fetchEnvelope<{ data: Row[]; pagination: Page }>(url);
 * return { rows: body.data ?? [], total: body.pagination?.total ?? 0 };
 */
export const fetchEnvelope = async <TBody, TRequest = undefined>(
  url: string,
  method = "GET",
  requestData?: TRequest,
  headers?: Record<string, string>
): Promise<TBody> =>
  (await request(url, method, requestData, headers)) as TBody;
